import { layoutGraphicRenderPaths, layoutGraphicTextPath } from './layoutGraphicRenderGeometry';
import type {
  LayoutGraphicElement,
  LayoutGraphicFill,
  LayoutGraphicPoint,
  LayoutGraphicStroke,
  LayoutTextSymbol,
  PageRect,
} from './layoutTypes';

const DASHES: Readonly<Record<LayoutGraphicStroke['style'], readonly number[]>> = {
  solid: [],
  dash: [6, 4],
  dot: [1, 3],
  dashDot: [6, 3, 1, 3],
};

function canvasPoint(point: LayoutGraphicPoint, width: number, height: number) {
  return { x: point.x * width, y: point.y * height };
}

function beginPaths(
  ctx: CanvasRenderingContext2D,
  element: LayoutGraphicElement,
  width: number,
  height: number,
): void {
  ctx.beginPath();
  for (const candidate of layoutGraphicRenderPaths(element)) {
    candidate.points.forEach((point, index) => {
      const local = canvasPoint(point, width, height);
      if (index === 0) ctx.moveTo(local.x, local.y);
      else ctx.lineTo(local.x, local.y);
    });
    if (candidate.closed) ctx.closePath();
  }
}

function paintPaths(
  ctx: CanvasRenderingContext2D,
  fill: LayoutGraphicFill | null,
  stroke: LayoutGraphicStroke,
  pxPerPt: number,
): void {
  if (fill?.color) {
    ctx.save();
    ctx.globalAlpha = fill.opacityPercent / 100;
    ctx.fillStyle = fill.color;
    ctx.fill(fill.rule);
    ctx.restore();
  }
  if (stroke.color && stroke.widthPt > 0) {
    ctx.save();
    ctx.globalAlpha = stroke.opacityPercent / 100;
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.widthPt * pxPerPt;
    ctx.lineCap = stroke.cap;
    ctx.lineJoin = stroke.join;
    ctx.setLineDash(DASHES[stroke.style].map((value) => value * pxPerPt));
    ctx.stroke();
    ctx.restore();
  }
}

function markerPath(
  ctx: CanvasRenderingContext2D,
  shape: Extract<LayoutGraphicElement, { category: 'point' }>['marker']['shape'],
  radius: number,
): void {
  ctx.beginPath();
  if (shape === 'circle') {
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
  } else if (shape === 'square') {
    ctx.rect(-radius, -radius, radius * 2, radius * 2);
  } else if (shape === 'diamond') {
    ctx.moveTo(0, -radius);
    ctx.lineTo(radius, 0);
    ctx.lineTo(0, radius);
    ctx.lineTo(-radius, 0);
    ctx.closePath();
  } else if (shape === 'triangle') {
    ctx.moveTo(0, -radius);
    ctx.lineTo(radius, radius);
    ctx.lineTo(-radius, radius);
    ctx.closePath();
  } else {
    const arm = radius * 0.32;
    if (shape === 'cross') {
      ctx.rect(-arm, -radius, arm * 2, radius * 2);
      ctx.rect(-radius, -arm, radius * 2, arm * 2);
    } else {
      ctx.moveTo(-radius, -radius + arm);
      ctx.lineTo(-radius + arm, -radius);
      ctx.lineTo(0, -arm);
      ctx.lineTo(radius - arm, -radius);
      ctx.lineTo(radius, -radius + arm);
      ctx.lineTo(arm, 0);
      ctx.lineTo(radius, radius - arm);
      ctx.lineTo(radius - arm, radius);
      ctx.lineTo(0, arm);
      ctx.lineTo(-radius + arm, radius);
      ctx.lineTo(-radius, radius - arm);
      ctx.lineTo(-arm, 0);
      ctx.closePath();
    }
  }
}

function arrowhead(
  ctx: CanvasRenderingContext2D,
  tip: LayoutGraphicPoint,
  neighbor: LayoutGraphicPoint,
  style: 'open' | 'closed',
  size: number,
  color: string,
): void {
  const angle = Math.atan2(tip.y - neighbor.y, tip.x - neighbor.x);
  ctx.save();
  ctx.translate(tip.x, tip.y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(-size, -size * 0.55);
  ctx.moveTo(0, 0);
  ctx.lineTo(-size, size * 0.55);
  if (style === 'closed') {
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  }
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.restore();
}

function casedText(value: string, symbol: LayoutTextSymbol): string {
  if (symbol.textCase === 'uppercase' || symbol.textCase === 'smallCaps') {
    return value.toUpperCase();
  }
  return symbol.textCase === 'lowercase' ? value.toLowerCase() : value;
}

function fontFor(symbol: LayoutTextSymbol, pxPerPt: number): string {
  const bold = symbol.fontStyle === 'bold' || symbol.fontStyle === 'boldItalic';
  const italic = symbol.fontStyle === 'italic' || symbol.fontStyle === 'boldItalic';
  return `${italic ? 'italic ' : ''}${bold ? '700 ' : ''}${Math.max(
    1,
    symbol.sizePt * pxPerPt,
  )}px ${symbol.fontFamily}`;
}

function drawDecoratedText(
  ctx: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  symbol: LayoutTextSymbol,
  pxPerPt: number,
  align: CanvasTextAlign,
  angle = symbol.angle,
): void {
  const text = casedText(value, symbol);
  const adjustment = symbol.positionAdjustment === 'superscript'
    ? -symbol.sizePt * 0.35
    : symbol.positionAdjustment === 'subscript' ? symbol.sizePt * 0.25 : 0;
  ctx.save();
  ctx.globalAlpha = symbol.opacityPercent / 100;
  ctx.font = fontFor(symbol, pxPerPt);
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
  ctx.translate(
    x + symbol.offsetXPt * pxPerPt,
    y + (symbol.offsetYPt + adjustment) * pxPerPt,
  );
  ctx.rotate((angle * Math.PI) / 180);
  const metrics = ctx.measureText(text);
  const left = align === 'center' ? -metrics.width / 2 : align === 'right' ? -metrics.width : 0;
  const fontHeight = symbol.sizePt * pxPerPt * 1.2;
  if (symbol.callout.enabled) {
    const padding = symbol.callout.paddingPt * pxPerPt;
    ctx.fillStyle = symbol.callout.fillColor;
    ctx.fillRect(left - padding, -fontHeight - padding, metrics.width + padding * 2, fontHeight + padding * 2);
    ctx.strokeStyle = symbol.callout.outlineColor;
    ctx.lineWidth = Math.max(1, symbol.outlineWidthPt * pxPerPt);
    ctx.strokeRect(left - padding, -fontHeight - padding, metrics.width + padding * 2, fontHeight + padding * 2);
  }
  if (symbol.shadow.enabled) {
    ctx.shadowColor = symbol.shadow.color;
    ctx.shadowOffsetX = symbol.shadow.offsetXPt * pxPerPt;
    ctx.shadowOffsetY = symbol.shadow.offsetYPt * pxPerPt;
  }
  if (symbol.halo.enabled) {
    ctx.strokeStyle = symbol.halo.color;
    ctx.lineJoin = 'round';
    ctx.lineWidth = symbol.halo.sizePt * pxPerPt * 2;
    ctx.strokeText(text, 0, 0);
  }
  if (symbol.outlineWidthPt > 0) {
    ctx.strokeStyle = symbol.outlineColor;
    ctx.lineWidth = symbol.outlineWidthPt * pxPerPt * 2;
    ctx.strokeText(text, 0, 0);
  }
  ctx.fillStyle = symbol.color;
  ctx.fillText(text, 0, 0);
  ctx.shadowColor = 'transparent';
  if (symbol.underline || symbol.strikethrough) {
    ctx.strokeStyle = symbol.color;
    ctx.lineWidth = Math.max(1, symbol.sizePt * pxPerPt * 0.06);
    const rules = [
      symbol.underline ? symbol.sizePt * pxPerPt * 0.12 : null,
      symbol.strikethrough ? -symbol.sizePt * pxPerPt * 0.3 : null,
    ].filter((rule): rule is number => rule !== null);
    for (const ruleY of rules) {
      ctx.beginPath();
      ctx.moveTo(left, ruleY);
      ctx.lineTo(left + metrics.width, ruleY);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split(/\r?\n/)) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(next).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = next;
      }
    }
    lines.push(line);
  }
  return lines;
}

function drawAreaText(
  ctx: CanvasRenderingContext2D,
  element: Extract<LayoutGraphicElement, { category: 'text' }>,
  width: number,
  height: number,
  pxPerPt: number,
): void {
  const margin = element.text.marginPt * pxPerPt;
  const gap = element.text.columnGapPt * pxPerPt;
  const columns = Math.max(1, element.text.columnCount);
  const columnWidth = Math.max(1, (width - margin * 2 - gap * (columns - 1)) / columns);
  let symbol = element.textSymbol;
  ctx.font = fontFor(symbol, pxPerPt);
  let lines = wrapText(ctx, casedText(element.text.text, symbol), columnWidth);
  const availableHeight = Math.max(1, height - margin * 2);
  if (element.text.overflow === 'shrink') {
    while (
      symbol.sizePt > 4
      && lines.length * symbol.sizePt * pxPerPt * 1.25 > availableHeight * columns
    ) {
      symbol = { ...symbol, sizePt: symbol.sizePt - 0.5 };
      ctx.font = fontFor(symbol, pxPerPt);
      lines = wrapText(ctx, casedText(element.text.text, symbol), columnWidth);
    }
  }
  if (element.text.overflow === 'clip') {
    ctx.save();
    beginPaths(ctx, element, width, height);
    ctx.clip(element.fill.rule);
  }
  const lineHeight = symbol.sizePt * pxPerPt * 1.25;
  const linesPerColumn = Math.max(1, Math.floor(availableHeight / lineHeight));
  lines.forEach((line, index) => {
    const column = Math.min(columns - 1, Math.floor(index / linesPerColumn));
    const row = index % linesPerColumn;
    const columnX = margin + column * (columnWidth + gap);
    const align = symbol.horizontalAlignment === 'center' ? 'center'
      : symbol.horizontalAlignment === 'justify' ? 'left' : symbol.horizontalAlignment;
    const x = align === 'center' ? columnX + columnWidth / 2
      : align === 'right' ? columnX + columnWidth : columnX;
    const usedHeight = Math.min(linesPerColumn, lines.length - column * linesPerColumn) * lineHeight;
    const top = symbol.verticalAlignment === 'middle' ? (height - usedHeight) / 2
      : symbol.verticalAlignment === 'bottom' ? height - margin - usedHeight : margin;
    if (symbol.horizontalAlignment === 'justify' && line.includes(' ')) {
      const words = line.split(' ');
      const wordsWidth = words.reduce((sum, word) => sum + ctx.measureText(word).width, 0);
      const wordGap = (columnWidth - wordsWidth) / Math.max(1, words.length - 1);
      let wordX = columnX;
      for (const word of words) {
        drawDecoratedText(
          ctx,
          word,
          wordX,
          top + (row + 1) * lineHeight,
          symbol,
          pxPerPt,
          'left',
        );
        wordX += ctx.measureText(word).width + wordGap;
      }
    } else {
      drawDecoratedText(ctx, line, x, top + (row + 1) * lineHeight, symbol, pxPerPt, align);
    }
  });
  if (element.text.overflow === 'clip') ctx.restore();
}

function pathMetrics(points: readonly LayoutGraphicPoint[]) {
  const lengths: number[] = [0];
  for (let index = 1; index < points.length; index += 1) {
    lengths.push(lengths.at(-1)! + Math.hypot(
      points[index].x - points[index - 1].x,
      points[index].y - points[index - 1].y,
    ));
  }
  return lengths;
}

function pointAlong(
  points: readonly LayoutGraphicPoint[],
  lengths: readonly number[],
  distance: number,
) {
  const total = lengths.at(-1) ?? 0;
  const target = Math.max(0, Math.min(total, distance));
  let index = 1;
  while (index < lengths.length - 1 && lengths[index] < target) index += 1;
  const start = points[index - 1];
  const end = points[index];
  const span = Math.max(0.00001, lengths[index] - lengths[index - 1]);
  const t = (target - lengths[index - 1]) / span;
  return {
    x: start.x + (end.x - start.x) * t,
    y: start.y + (end.y - start.y) * t,
    angle: Math.atan2(end.y - start.y, end.x - start.x),
  };
}

function drawPathText(
  ctx: CanvasRenderingContext2D,
  element: Extract<LayoutGraphicElement, { category: 'text' }>,
  width: number,
  height: number,
  pxPerPt: number,
): void {
  const points = layoutGraphicTextPath(element).map((point) => canvasPoint(point, width, height));
  if (points.length < 2) return;
  const symbol = element.textSymbol;
  const text = casedText(element.text.text, symbol);
  ctx.font = fontFor(symbol, pxPerPt);
  const glyphs = [...text].map((glyph) => ({ glyph, width: ctx.measureText(glyph).width }));
  const textWidth = glyphs.reduce((sum, glyph) => sum + glyph.width, 0);
  const lengths = pathMetrics(points);
  const total = lengths.at(-1) ?? 0;
  const justifyGap = symbol.horizontalAlignment === 'justify' && glyphs.length > 1
    ? Math.max(0, (total - textWidth) / (glyphs.length - 1))
    : 0;
  let cursor = symbol.horizontalAlignment === 'right'
    ? total - textWidth
    : symbol.horizontalAlignment === 'center'
      ? (total - textWidth) / 2
      : 0;
  for (const glyph of glyphs) {
    const center = cursor + glyph.width / 2;
    if (center >= 0 && center <= total) {
      const position = pointAlong(points, lengths, center);
      const offset = element.text.pathOffset * pxPerPt;
      drawDecoratedText(
        ctx,
        glyph.glyph,
        position.x - Math.sin(position.angle) * offset,
        position.y + Math.cos(position.angle) * offset,
        symbol,
        pxPerPt,
        'center',
        (position.angle * 180) / Math.PI + symbol.angle,
      );
    }
    cursor += glyph.width + justifyGap;
  }
}

/** Paints one static layout graphic into an already scaled page canvas. */
export function renderLayoutGraphicToCanvas(
  ctx: CanvasRenderingContext2D,
  element: LayoutGraphicElement,
  rect: PageRect,
  dpi: number,
): void {
  if (!element.visible || rect.width <= 0 || rect.height <= 0) return;
  const pxPerPt = dpi / 72;
  ctx.save();
  ctx.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
  ctx.rotate((element.rotation * Math.PI) / 180);
  ctx.scale(element.flipX ? -1 : 1, element.flipY ? -1 : 1);
  ctx.translate(-rect.width / 2, -rect.height / 2);

  if (element.category === 'point') {
    ctx.save();
    ctx.translate(rect.width / 2, rect.height / 2);
    markerPath(ctx, element.marker.shape, element.marker.sizePt * pxPerPt / 2);
    paintPaths(ctx, element.marker.fill, element.marker.stroke, pxPerPt);
    ctx.restore();
  } else {
    beginPaths(ctx, element, rect.width, rect.height);
    const fill = 'fill' in element ? element.fill : null;
    paintPaths(ctx, fill, element.stroke, pxPerPt);
    if (element.category === 'arrow' || element.category === 'line') {
      const renderPath = layoutGraphicRenderPaths(element)[0];
      const points = renderPath?.points.map((point) => canvasPoint(point, rect.width, rect.height)) ?? [];
      if (points.length >= 2) {
        const heads = element.arrowheads;
        const color = element.category === 'arrow'
          ? element.fill.color ?? element.stroke.color
          : element.stroke.color;
        if (color) {
          ctx.save();
          ctx.globalAlpha = element.stroke.opacityPercent / 100;
          ctx.lineWidth = Math.max(1, element.stroke.widthPt * pxPerPt);
          if (heads.start !== 'none') {
            arrowhead(ctx, points[0], points[1], heads.start, heads.sizePt * pxPerPt, color);
          }
          if (heads.end !== 'none') {
            arrowhead(ctx, points.at(-1)!, points.at(-2)!, heads.end, heads.sizePt * pxPerPt, color);
          }
          ctx.restore();
        }
      }
    }
    if (element.category === 'text') {
      if (element.graphicType === 'rectangleText' || element.graphicType === 'polygonText') {
        drawAreaText(ctx, element, rect.width, rect.height, pxPerPt);
      } else {
        drawPathText(ctx, element, rect.width, rect.height, pxPerPt);
      }
    }
  }
  ctx.restore();
}
